import { Fragment, useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { PlayerCardData, StatLine, SplitStatLine } from '../types'
import { avg3 } from '../format'
import { fmtIp } from '@/features/league/_shared/stats'
import { cardTheadRow, cardBodyRow, cardTotalRow, cardStickyBg } from '../cardTableStyles'
import { CardTeamLink } from '../CardTeamLink'

const two = (v: number) => v.toFixed(2)

// tier 0 = snapshot (the 5-col Summary glance), 1 = standard, 2 = full (Full Stats tab).
// A column shows when the view's tier is >= the column's tier. Summary renders
// tier 0 only; Full Stats renders tier 2 (which subsumes tier 1).
export type StatTier = 0 | 1 | 2
type Col = { key: keyof StatLine; label: string; fmt?: (v: number) => string; tier: StatTier }

const BAT_COLS: Col[] = [
  { key: 'g', label: 'G', tier: 0 },
  { key: 'ab', label: 'AB', tier: 1 },
  { key: 'r', label: 'R', tier: 1 },
  { key: 'h', label: 'H', tier: 1 },
  { key: 'doubles', label: '2B', tier: 2 },
  { key: 'triples', label: '3B', tier: 2 },
  { key: 'hr', label: 'HR', tier: 1 },
  { key: 'rbi', label: 'RBI', tier: 1 },
  { key: 'bb', label: 'BB', tier: 1 },
  { key: 'so', label: 'SO', tier: 2 },
  { key: 'sb', label: 'SB', tier: 1 },
  { key: 'cs', label: 'CS', tier: 2 },
  { key: 'avg', label: 'AVG', fmt: avg3, tier: 0 },
  { key: 'obp', label: 'OBP', fmt: avg3, tier: 0 },
  { key: 'slg', label: 'SLG', fmt: avg3, tier: 0 },
  { key: 'ops', label: 'OPS', fmt: avg3, tier: 0 },
]
const PIT_COLS: Col[] = [
  { key: 'g', label: 'G', tier: 0 },
  { key: 'gs', label: 'GS', tier: 0 },
  { key: 'w', label: 'W', tier: 1 },
  { key: 'l', label: 'L', tier: 1 },
  { key: 'sv', label: 'SV', tier: 1 },
  { key: 'ip', label: 'IP', fmt: fmtIp, tier: 0 },
  { key: 'h', label: 'H', tier: 2 },
  { key: 'r', label: 'R', tier: 2 },
  { key: 'er', label: 'ER', tier: 2 },
  { key: 'bb', label: 'BB', tier: 1 },
  { key: 'so', label: 'K', tier: 1 },
  { key: 'era', label: 'ERA', fmt: two, tier: 0 },
  { key: 'whip', label: 'WHIP', fmt: two, tier: 0 },
]

function cell(line: StatLine, col: Col) {
  const v = line[col.key]
  if (typeof v !== 'number') return '—'
  return col.fmt ? col.fmt(v) : String(v)
}

// A cell is flagged when the row's season (its year label) is in the flag map
// and the column's stat is one the player led/recorded that season. Shared by
// the leader (`statLeaders`) and record (`statRecords`) maps.
function isFlaggedCell(
  flags: Record<number, string[]> | undefined,
  label: string | undefined,
  key: keyof StatLine,
): boolean {
  if (!flags || !label) return false
  const yr = Number(label)
  return Number.isFinite(yr) && (flags[yr]?.includes(key as string) ?? false)
}

// A flagged cell bolds the same way whether it's a league leader or a league
// record (Russ 2026-06-14: records bold like leaders); the record gets the
// stronger "League record" tooltip when both apply.
function flagEmphasis(
  leaders: Record<number, string[]> | undefined,
  records: Record<number, string[]> | undefined,
  label: string | undefined,
  key: keyof StatLine,
): { bold: boolean; title?: string } {
  const rec = isFlaggedCell(records, label, key)
  const lead = isFlaggedCell(leaders, label, key)
  if (rec) return { bold: true, title: 'League record this season' }
  if (lead) return { bold: true, title: 'Led the league this season' }
  return { bold: false }
}

export function StatTable({ isPitcher, rows, tier, hideCols, leaders, records }: { isPitcher: boolean; rows: StatLine[]; tier: StatTier; hideCols?: (keyof StatLine)[]; leaders?: Record<number, string[]>; records?: Record<number, string[]> }) {
  const hide = new Set(hideCols)
  const cols = (isPitcher ? PIT_COLS : BAT_COLS).filter((c) => c.tier <= tier && !hide.has(c.key))
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-max text-sm tabular-nums">
        <thead>
          <tr className={cardTheadRow}>
            {/* Year — sticky left so it stays pinned while wide tables scroll horizontally */}
            <th className="sticky left-0 z-20 !bg-white py-1.5 pr-3 text-left font-semibold">Year</th>
            {cols.map((c) => <th key={c.label} className="px-2 py-1.5 text-right font-semibold">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((line, i) => {
            const isTotal = line.label === 'Career'
            return (
              <tr key={i} className={isTotal ? cardTotalRow : cardBodyRow}>
                <td className={`sticky left-0 z-10 ${isTotal ? '!bg-slate-100' : cardStickyBg(i)} py-1.5 pr-3 text-left whitespace-nowrap`}><span className="font-semibold">{line.label}</span><CardTeamLink teamId={line.teamId} team={line.team} /></td>
                {cols.map((c) => {
                  const emph = flagEmphasis(leaders, records, line.label, c.key)
                  return (
                    <td
                      key={c.label}
                      className={cn('px-2 py-1.5 text-right', emph.bold && 'font-bold text-navy')}
                      title={emph.title}
                    >
                      {cell(line, c)}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// A row in the merged table: a plain StatLine, optionally carrying split +
// season (history rows render with a muted year prefix) or a careerTotal flag
// (renders a bold "Career" total row per split).
type SummaryRow = StatLine & { split?: string; season?: number | null; careerTotal?: boolean }

// One row group inside the merged Summary stats table. `hide` columns render
// as "—" for the group's rows (e.g. Situational is G-only — no AB). `history`
// = prior-year rows revealed by the group's own chevron. `career` = per-split
// career totals pinned to the bottom of the group (always shown, like the
// snapshot's Career row).
interface StatGroup {
  key: string
  label: string | null
  rows: SummaryRow[]
  history?: SummaryRow[]
  career?: SummaryRow[]
  hide?: Set<keyof StatLine>
  // Override the table's column set for this group. Used for a pitcher's L/R
  // splits, whose rows carry the OPPONENT's batting line (what hitters did vs
  // them) rather than a pitching line — they need the batting slash columns, not
  // the pitcher columns the rest of the table uses. Rendered with its own small
  // column sub-header so the values read against the right labels.
  cols?: Col[]
}

function normalizeSplits(rows: (StatLine | SplitStatLine)[]): SummaryRow[] {
  return rows.map((r) => ({ ...r, label: (r as SplitStatLine).split ?? r.label }))
}

// Prior years newest-first, preserving the within-season row order the API
// sent (Home before Away, vs R before vs L).
function sortHistory(rows: SplitStatLine[]): SummaryRow[] {
  return normalizeSplits([...rows].sort((a, b) => (b.season ?? 0) - (a.season ?? 0)))
}

const VS_HAND = /^vs\s/i

// Situational handedness labels are opponent-relative: a batter faces pitchers
// (v RHP / v LHP), a pitcher faces batters (v RHB / v LHB). Normalize whatever
// the API sends ("vs Righty", "vs RHP", …) to the compact opponent-aware form.
function handednessLabel(split: string, isPitcher: boolean): string {
  const opp = isPitcher ? 'B' : 'P' // pitcher faces batters; batter faces pitchers
  if (/right|rh[pb]?/i.test(split)) return `v RH${opp}`
  if (/left|lh[pb]?/i.test(split)) return `v LH${opp}`
  return split
}

// The merged Summary stats table: Snapshot (current + career), Situational
// (vs R / vs L) and Splits (Home / Away) share ONE column header at the
// condensed slash-line tier — three sections' worth of data, one table's worth
// of chrome. Tiny group labels keep orientation. Two expansion axes (both keep
// the condensed 5-column glance set — widening to the full column set is the
// Full Stats tab's job):
// - corner-cell chevron: grows the snapshot to all seasons (full history)
// - each group label's chevron: walks that group back through prior seasons
//   (yearly rows, muted year prefix)
// The full firehose stays on the Full Stats tab.
export function SummaryStatsSection({ player }: { player: PlayerCardData }) {
  const [open, setOpen] = useState(false)
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({})
  const { currentSeason, career } = player.stats
  const statLeaders = player.statLeaders
  const statRecords = player.statRecords
  const seasons = player.seasonStats ?? []
  // Tables read newest-first (reverse chronological); Career totals stay pinned
  // to the bottom as the summing row. Condensed glance = current season only;
  // expanding reveals all prior seasons AND the Career total.
  const yearOf = (s: StatLine) => Number(s.label) || 0
  const seasonsDesc = [...seasons].sort((a, b) => yearOf(b) - yearOf(a))
  const snapshot: StatLine[] = open
    ? [...seasonsDesc, ...(career ? [career] : [])]
    : currentSeason
      ? [currentSeason]
      : []
  // The current-season situational/splits rows carry no season of their own, so
  // stamp the current year onto them — both to label them like their history
  // rows (parity with the snapshot's year-labeled current row) and so the
  // year-boundary dividers land correctly.
  const currentYear =
    currentSeason?.label && /^\d{4}$/.test(currentSeason.label) ? Number(currentSeason.label) : null
  const stampYear = (rows: SummaryRow[]): SummaryRow[] =>
    currentYear == null ? rows : rows.map((r) => ({ ...r, season: r.season ?? currentYear }))
  // Summary situational = handedness splits only (vs RHP / vs LHP); other
  // situations (RISP, etc.) live on the Full Stats tab. G, not AB.
  // Filter to handedness splits on the raw label, THEN relabel to the compact
  // opponent-aware form (the relabel drops the "vs " the filter keys off of).
  const relabel = (rows: SplitStatLine[]): SplitStatLine[] =>
    rows.filter((s) => VS_HAND.test(s.split)).map((s) => ({ ...s, split: handednessLabel(s.split, player.isPitcher) }))
  const situational = relabel(player.situationalStats ?? [])
  const situationalHistory = relabel(player.situationalStatsHistory ?? [])
  const situationalCareer = relabel(player.situationalStatsCareer ?? [])
  const splits = player.splitStats ?? []
  const splitsHistory = player.splitStatsHistory ?? []
  const splitsCareer = player.splitStatsCareer ?? []

  const groups: StatGroup[] = [
    { key: 'snapshot', label: null, rows: snapshot },
    {
      key: 'situational',
      // Summary shows only the handedness rows, so name it for that (the full
      // situational set — RISP, etc. — lives on the Full Stats tab as "Situational").
      label: 'L/R Splits',
      rows: stampYear(normalizeSplits(situational)),
      history: sortHistory(situationalHistory),
      career: normalizeSplits(situationalCareer),
      hide: new Set<keyof StatLine>(['ab']),
      // For a pitcher these rows are the opponent's batting line (v RHB / v LHB),
      // so render them under the batting slash columns instead of the pitcher
      // columns — otherwise every cell is blank against G/GS/IP/ERA/WHIP.
      cols: player.isPitcher ? BAT_COLS.filter((c) => c.tier <= 0) : undefined,
    },
    {
      key: 'splits',
      label: 'Home/Away Splits',
      rows: stampYear(normalizeSplits(splits)),
      history: sortHistory(splitsHistory),
      career: normalizeSplits(splitsCareer),
    },
  ].filter((g) => g.rows.length > 0)

  // Summary always shows the 5-column glance set (tier 0). The corner chevron
  // only grows the snapshot to all seasons (full history) via `open` above —
  // it no longer widens the columns. The full column set lives on Full Stats.
  const tier: StatTier = 0
  const cols = (player.isPitcher ? PIT_COLS : BAT_COLS).filter((c) => c.tier <= tier)
  const hasAny = groups.length > 0

  return (
    <section className="border-b border-slate-200 pt-2 pb-3">
      <div className="overflow-x-auto">
        <table className="w-full min-w-max text-sm tabular-nums">
          <thead>
            <tr className={cardTheadRow}>
              <th className="sticky left-0 z-20 !bg-white py-1 pr-3 text-left font-semibold">
                <button
                  type="button"
                  onClick={() => setOpen((o) => !o)}
                  aria-expanded={open}
                  className="group -ml-1 flex items-center gap-1 rounded px-1 text-left transition-colors hover:bg-slate-100"
                >
                  {/* Navy chevron (not faint slate) signals the expander; the
                      explicit "show all seasons" text cue was removed from the
                      Summary tab per Russ 2026-06-13. */}
                  <ChevronRight className={cn('h-4 w-4 shrink-0 text-navy transition-transform', open && 'rotate-90')} />
                  <span className="text-xs font-bold uppercase tracking-wide text-navy">Current Stats</span>
                </button>
              </th>
              {cols.map((c) => <th key={c.label} className="px-2 py-1 text-right font-semibold">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {!hasAny && (
              <tr>
                <td colSpan={cols.length + 1} className="py-1.5 text-sm text-slate-500">No stats to display.</td>
              </tr>
            )}
            {groups.map((group) => {
              const groupOpen = Boolean(openGroups[group.key])
              // Expandable if there's prior-season history or per-split career
              // totals to reveal.
              const canExpand =
                (group.history?.length ?? 0) > 0 || (group.career?.length ?? 0) > 0
              // Condensed glance = current rows only. Expanding the group reveals
              // its prior-season history AND the per-split career totals (pinned
              // to the bottom). Career stays out of the condensed view.
              const rows = groupOpen
                ? [...group.rows, ...(group.history ?? []), ...(group.career ?? [])]
                : group.rows
              // Per-group column set (pitcher L/R overrides to the batting slash
              // columns); falls back to the shared table columns.
              const gCols = group.cols ?? cols
              return (
                <Fragment key={group.key}>
                  {group.label && (
                    <tr>
                      <td colSpan={cols.length + 1} className="pt-2 pb-0.5">
                        <button
                          type="button"
                          onClick={() => canExpand && setOpenGroups((g) => ({ ...g, [group.key]: !g[group.key] }))}
                          aria-expanded={groupOpen}
                          className={cn(
                            // sticky left-0: pin the section label to the frozen
                            // first column so it stays aligned with the row labels
                            // below it on horizontal scroll (matches the data rows'
                            // sticky-left cell — without this the label scrolls off
                            // while the rest of the first column stays put).
                            'group sticky left-0 -ml-1 flex w-fit items-center gap-1 rounded px-1 text-left transition-colors',
                            canExpand ? 'hover:bg-slate-100' : 'cursor-default',
                          )}
                        >
                          <ChevronRight
                            className={cn(
                              'h-4 w-4 shrink-0 text-navy transition-transform',
                              groupOpen && 'rotate-90',
                              !canExpand && 'hidden',
                            )}
                          />
                          <span className="text-xs font-bold uppercase tracking-wide text-navy">{group.label}</span>
                        </button>
                      </td>
                    </tr>
                  )}
                  {/* A group that overrides the table columns (pitcher L/R =
                      opponent slash line) needs its own small column header so its
                      values read against the right labels, not the table's. */}
                  {group.cols && (
                    <tr className="text-slate-500">
                      <td className="sticky left-0 z-10 !bg-white py-0.5 pr-3 text-left text-[11px] font-semibold uppercase tracking-wide">Allowed</td>
                      {gCols.map((c) => (
                        <td key={c.label} className="px-2 py-0.5 text-right text-[11px] font-semibold">{c.label}</td>
                      ))}
                    </tr>
                  )}
                  {rows.map((line, i) => {
                    const isTotal = line.label === 'Career' || Boolean(line.careerTotal)
                    // In the grouped (situational/splits) tables each season has
                    // multiple rows (vs RHP/vs LHP, Home/Away). Mark the first
                    // row of a new season with a heavier rule so each year reads
                    // as one block. Snapshot is one-row-per-year, so it opts out.
                    const newYear =
                      group.history != null &&
                      i > 0 &&
                      (line.season ?? null) !== (rows[i - 1].season ?? null)
                    return (
                      <tr
                        key={`${group.key}-${i}`}
                        className={
                          isTotal
                            ? cardTotalRow
                            : cn(
                                'group text-slate-700 hover:bg-slate-200',
                                // Explicit stripe (not CSS odd:) — group-header rows
                                // sit between data rows and would scramble :nth-child.
                                i % 2 === 0 ? 'bg-[#f0f0f0]' : 'bg-white',
                                newYear && 'border-t-2 border-slate-300',
                              )
                        }
                      >
                        <td className={`sticky left-0 z-10 ${isTotal ? '!bg-slate-100' : cardStickyBg(i)} py-1.5 pr-3 text-left whitespace-nowrap`}>
                          {/* Year/Career prefix inherits the row color so the left
                              column reads one shade across all sections (matches the
                              snapshot, whose year is the row label). */}
                          {/* Bold the YEAR token so this matches the Stats-tab
                              tables: snapshot rows (label is the year) bold the
                              label; grouped rows bold the season prefix instead
                              (their label is the split descriptor). Career rows
                              are already bold via the row. */}
                          {line.careerTotal
                            ? <span className="tabular-nums">Career </span>
                            : line.season != null && <span className="font-semibold tabular-nums">{line.season} </span>}
                          <span className={line.careerTotal || line.season != null ? '' : 'font-semibold'}>{line.label}</span>
                          <CardTeamLink teamId={line.teamId} team={line.team} />
                        </td>
                        {gCols.map((c) => {
                          // Leader/record bold only applies to the snapshot's season
                          // rows (label is the year); split rows have non-numeric
                          // labels so flagEmphasis naturally skips them.
                          const emph = flagEmphasis(statLeaders, statRecords, line.label, c.key)
                          return (
                            <td
                              key={c.label}
                              className={cn('px-2 py-1.5 text-right', emph.bold && 'font-bold text-navy')}
                              title={emph.title}
                            >
                              {group.hide?.has(c.key) ? '—' : cell(line, c)}
                            </td>
                          )
                        })}
                      </tr>
                    )
                  })}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}
